import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IPart, IPartCategoryDefault, IPartCategoryDefaultResponse } from "../models/Part";

export default function usePartDefaultCategories({ part, enabled = true }: { part: IPart, enabled?: boolean }) {
  const { get, post, put, patch, postDocs } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 30; //30 minutes

  const patchPagedTableDefaults = (categoryId: string | null | undefined, incomingDefaults: any) => {
    if (!categoryId || !incomingDefaults) return;

    const pagedQueries = queryClient.getQueriesData(["pagedTable"]);
    pagedQueries.forEach(([queryKey]) => {
      queryClient.setQueryData(queryKey, (currentData: any) => {
        if (!currentData?.data?.rows) return currentData;

        return {
          ...currentData,
          data: {
            ...currentData.data,
            rows: currentData.data.rows.map((row: any) => {
              if (row.categoryId !== categoryId) return row;

              const previousDefaults = row.category?.partCategoryDefaults?.[0] ?? {};
              return {
                ...row,
                category: {
                  ...(row.category || {}),
                  partCategoryDefaults: [
                    {
                      ...previousDefaults,
                      ...incomingDefaults,
                    },
                  ],
                },
              };
            }),
          },
        };
      });
    });
  };

  // console.log("enabled", enabled);

  const defaultPartCategory = useQuery(
    ["parts", part.categoryId, "defaultcategory"],
    () => get<IPartCategoryDefaultResponse>(`/part/defaultcategory/${part.categoryId}`),
    {
      enabled: !!part.categoryId && enabled,
      staleTime,
    }
  );

  const saveDefaultCategory = useMutation(
    (defaults: IPartCategoryDefault) => post(`/part/defaultcategory/${part.categoryId}`, defaults),
    {
      onMutate: async (defaults: IPartCategoryDefault) => {
        await queryClient.cancelQueries(["pagedTable"]);
        const previousPagedQueries = queryClient.getQueriesData(["pagedTable"]);

        patchPagedTableDefaults(part.categoryId, defaults);

        return { previousPagedQueries };
      },
      onError: (_error, _defaults, context: any) => {
        context?.previousPagedQueries?.forEach(([queryKey, oldData]: any) => {
          queryClient.setQueryData(queryKey, oldData);
        });
      },
      onSuccess: (response) => {
        const savedDefaults = response?.data?.data ?? response?.data;
        if (savedDefaults) {
          patchPagedTableDefaults(part.categoryId, savedDefaults);
        }

        queryClient.invalidateQueries(["parts"]);
        queryClient.refetchQueries(["parts", "multiple"]);
        queryClient.refetchQueries(["categories"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const useDefaultPartCategory = useMutation(
    () => patch(`/part/${part.id}/defaultcategory`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts"]);
        queryClient.refetchQueries(["categories"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );


  return {
    useDefaultPartCategory,
    defaultPartCategory,
    saveDefaultCategory
  };
}
